import { useState, useEffect, useRef } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import {
  Search, Plus, Phone, Mail, MapPin, Edit2, Trash2, Archive, DollarSign,
  Target, Briefcase, X, Calendar, XCircle, Clock, MessageSquare,
  Link, Upload, Download, Loader, CheckCircle, AlertCircle, Send
} from 'lucide-react';
import ExpressJob from './ExpressJobWorkflow';
import CustomerCommunications from './CustomerCommunications';
import CustomerCardOnFile from './CustomerCardOnFile';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';
import { useFeatures } from './FeatureContext';
import { insertWithSync, updateWithSync, isOnline } from './offlineService';
import { SMSConsentInline } from './SMSConsent';
import { isOfflineModeEnabled, getCachedData, cacheData } from './offlineCache';

// Helper to safely get string value
const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

export default function CustomersLeads({ companyId, onNavigate, isMobile, textingEnabled }) {
  const { colors } = useTheme();
  const { showAlert, showConfirm, showSuccess, showError } = useModal();
  const { tier, getLimit } = useFeatures();
  const [activeTab, setActiveTab] = useState('all'); // all, leads, customers
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('customer'); // customer or lead
  const [editingItem, setEditingItem] = useState(null);
  const [showExpressJob, setShowExpressJob] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [showCommunications, setShowCommunications] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  // Per-tile activity: map of customer_id -> { jobs:[], quotes:[] }, plus the
  // set of customer ids whose history section is expanded ("More").
  const [activityByCustomer, setActivityByCustomer] = useState({});
  const [expandedActivity, setExpandedActivity] = useState({});
  const [sortBy, setSortBy] = useState('newest');
  const [savingRecord, setSavingRecord] = useState(false);
  const listRef = useRef(null);

  // Schedule modal state
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('9:00');
  const [scheduleDuration, setScheduleDuration] = useState(2);
  const [scheduleJobType, setScheduleJobType] = useState('');
  const [scheduling, setScheduling] = useState(false);
  // Signup link modal state
  const [showSignupLinkModal, setShowSignupLinkModal] = useState(false);
  const [signupPhone, setSignupPhone] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupMethod, setSignupMethod] = useState('sms'); // 'sms', 'email'
  const [sendingSignupLink, setSendingSignupLink] = useState(false);
  const [signupLinkSent, setSignupLinkSent] = useState(false);
  const [signupLinkUrl, setSignupLinkUrl] = useState('');

  // CSV Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [company, setCompany] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    type: 'residential',
    lead_source: '',
    sub_source: '',
    referrer_name: '',
    notes: '',
    sms_consent: true  // Default checked for SMS consent
  });

  // Lead source options
  const leadSourceOptions = [
    { value: '', label: 'Select source...' },
    { value: 'google', label: 'Google Search' },
    { value: 'google_ads', label: 'Google Ads' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'nextdoor', label: 'Nextdoor' },
    { value: 'yelp', label: 'Yelp' },
    { value: 'thumbtack', label: 'Thumbtack' },
    { value: 'angi', label: 'Angi / HomeAdvisor' },
    { value: 'referral', label: 'Referral' },
    { value: 'repeat', label: 'Repeat Customer' },
    { value: 'drive_by', label: 'Drive By / Yard Sign' },
    { value: 'website', label: 'Website' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (companyId) {
      loadData();
    } else {
      setLoading(false);
    }

  }, [companyId]);

  useEffect(() => {
    filterData();
  }, [data, activeTab, searchTerm, sortBy, showArchived]);

  // Escape key: close customer detail modal
  useEffect(() => {
    if (!showDetailModal) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        console.log('[Customers] Escape closing detail modal');
        setShowDetailModal(false);
        setDetailCustomer(null);
      }
    };
    window.addEventListener('keydown', handler, true); // capture phase
    return () => window.removeEventListener('keydown', handler, true);
  }, [showDetailModal]);

  // Batched fetch of every job + quote for the company, grouped by customer_id,
  // so each tile can show its activity without a per-tile query.
  const loadActivity = async () => {
    try {
      const [jobsRes, quotesRes] = await Promise.all([
        supabaseData
          .from('jobs')
          .select('id, job_number, job_type, status, scheduled_date, scheduled_time, quoted_value, actual_value, created_at, customer_id')
          .eq('company_id', companyId),
        supabaseData
          .from('quotes')
          .select('id, quote_number, status, total, created_at, customer_id, archived')
          .eq('company_id', companyId)
      ]);

      const map = {};
      (jobsRes.data || []).forEach(j => {
        if (!j.customer_id) return;
        (map[j.customer_id] = map[j.customer_id] || { jobs: [], quotes: [] }).jobs.push(j);
      });
      (quotesRes.data || []).forEach(q => {
        if (!q.customer_id) return;
        (map[q.customer_id] = map[q.customer_id] || { jobs: [], quotes: [] }).quotes.push(q);
      });
      setActivityByCustomer(map);
    } catch (err) {
      console.warn('CustomersLeads: activity load failed', err?.message);
    }
  };

  // Normalize a customer's jobs + quotes into color-coded active/history items.
  const getCustomerActivity = (customerId) => {
    const bucket = activityByCustomer[customerId];
    if (!bucket) return { active: [], history: [] };
    const active = [];
    const history = [];

    bucket.quotes.forEach(q => {
      const base = { key: `q-${q.id}`, kind: 'quote', date: q.created_at, amount: q.total };
      if (!q.archived && (q.status === 'draft' || q.status === 'sent')) {
        active.push({
          ...base,
          label: q.status === 'sent' ? 'Quote sent — awaiting signature' : 'Draft quote',
          color: q.status === 'sent' ? colors.warning : colors.textMuted
        });
      } else {
        // signed / scheduled / rejected / archived
        const won = q.status === 'signed' || q.status === 'scheduled';
        history.push({
          ...base,
          label: q.status === 'rejected' ? 'Quote declined'
            : won ? 'Quote accepted'
            : q.archived ? 'Quote archived' : `Quote ${q.status}`,
          color: q.status === 'rejected' ? colors.error : won ? colors.success : colors.textMuted
        });
      }
    });

    bucket.jobs.forEach(j => {
      const base = { key: `j-${j.id}`, kind: 'job', date: j.scheduled_date || j.created_at, amount: j.actual_value || j.quoted_value };
      if (j.status === 'completed') {
        history.push({ ...base, label: 'Job completed', color: colors.success });
      } else if (j.status === 'cancelled') {
        history.push({ ...base, label: 'Job cancelled', color: colors.error });
      } else if (j.status === 'in-progress') {
        active.push({ ...base, label: 'Job in progress', color: colors.info });
      } else if (!j.scheduled_date) {
        active.push({ ...base, label: 'Job — not scheduled', color: colors.warning });
      } else {
        active.push({ ...base, label: `Scheduled ${formatShortDate(j.scheduled_date)}`, color: colors.info });
      }
    });

    const byDateDesc = (a, b) => new Date(b.date || 0) - new Date(a.date || 0);
    active.sort(byDateDesc);
    history.sort(byDateDesc);
    return { active, history };
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    // Parse date-only strings (YYYY-MM-DD) as local to avoid a UTC off-by-one.
    const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
      ? new Date(`${dateStr}T00:00:00`)
      : new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const loadData = async (retryCount = 0) => {
    // Offline: serve from cache
    if (!navigator.onLine && isOfflineModeEnabled()) {
      const cachedCustomers = getCachedData('customers', companyId);
      const cachedLeads = getCachedData('leads', companyId);
      if (cachedCustomers || cachedLeads) {
        const customers = (cachedCustomers || []).map(c => ({ ...c, record_type: 'customer' }));
        const leads = (cachedLeads || []).map(l => ({ ...l, record_type: 'lead' }));
        setData([...leads, ...customers]);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);

      // Load company data for Stripe Connect status
      const { data: companyData } = await supabaseData
        .from('companies')
        .select('id, stripe_connect_id, stripe_charges_enabled, is_demo')
        .eq('id', companyId)
        .single();
      setCompany(companyData);

      // Load both customers and leads
      const [customersResult, leadsResult] = await Promise.all([
        supabaseData
          .from('customers')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false }),
        supabaseData
          .from('leads')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
      ]);

      if (customersResult.error || leadsResult.error) {
        const err = customersResult.error || leadsResult.error;
        if (retryCount < 1) {
          console.warn('CustomersLeads: query error, retrying...', err.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 500));
          return loadData(retryCount + 1);
        }
        // Fallback to cache
        if (isOfflineModeEnabled()) {
          const cachedCustomers = getCachedData('customers', companyId);
          const cachedLeads = getCachedData('leads', companyId);
          if (cachedCustomers || cachedLeads) {
            const customers = (cachedCustomers || []).map(c => ({ ...c, record_type: 'customer' }));
            const leads = (cachedLeads || []).map(l => ({ ...l, record_type: 'lead' }));
            setData([...leads, ...customers]);
            setLoading(false);
            return;
          }
        }
        throw err;
      }

      const customers = (customersResult.data || []).map(c => ({ ...c, record_type: 'customer' }));
      const leads = (leadsResult.data || []).map(l => ({ ...l, record_type: 'lead' }));

      const newData = [...leads, ...customers];
      setData(newData);

      // Load per-customer jobs + quotes for the tile activity strip (non-blocking)
      loadActivity();

      // Cache on success
      if (isOfflineModeEnabled()) {
        cacheData('customers', companyId, customersResult.data || []);
        cacheData('leads', companyId, leadsResult.data || []);
      }

      // Immediately update filtered data to prevent stale UI
      let filtered = [...newData];
      if (activeTab === 'leads') {
        filtered = filtered.filter(item => item.record_type === 'lead');
      } else if (activeTab === 'customers') {
        filtered = filtered.filter(item => item.record_type === 'customer');
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        filtered = filtered.filter(item => {
          const name = item.name || `${item.first_name || ''} ${item.last_name || ''}`;
          return (
            name.toLowerCase().includes(term) ||
            item.phone?.toLowerCase().includes(term) ||
            item.email?.toLowerCase().includes(term) ||
            item.address?.toLowerCase().includes(term)
          );
        });
      }
      filtered.sort((a, b) => {
        const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
        const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
        switch (sortBy) {
          case 'a-z': return nameA.localeCompare(nameB);
          case 'z-a': return nameB.localeCompare(nameA);
          case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
          case 'newest':
          default: return new Date(b.created_at) - new Date(a.created_at);
        }
      });
      setFilteredData(filtered);
    } catch (error) {
      showError('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = [...data];

    // Filter archived unless explicitly shown
    if (!showArchived) {
      filtered = filtered.filter(item => !item.archived_at);
    } else {
      filtered = filtered.filter(item => !!item.archived_at);
    }

    // Filter by tab
    if (activeTab === 'leads') {
      filtered = filtered.filter(item => item.record_type === 'lead');
    } else if (activeTab === 'customers') {
      filtered = filtered.filter(item => item.record_type === 'customer');
    }

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const name = item.name || `${item.first_name || ''} ${item.last_name || ''}`;
        return (
          name.toLowerCase().includes(term) ||
          item.phone?.toLowerCase().includes(term) ||
          item.email?.toLowerCase().includes(term) ||
          item.address?.toLowerCase().includes(term)
        );
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const nameA = (a.name || `${a.first_name || ''} ${a.last_name || ''}`).trim().toLowerCase();
      const nameB = (b.name || `${b.first_name || ''} ${b.last_name || ''}`).trim().toLowerCase();
      switch (sortBy) {
        case 'a-z': return nameA.localeCompare(nameB);
        case 'z-a': return nameB.localeCompare(nameA);
        case 'oldest': return new Date(a.created_at) - new Date(b.created_at);
        case 'newest':
        default: return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    setFilteredData(filtered);
  };

  const openCustomerDetail = async (customer) => {
    setDetailCustomer(customer);
    setShowDetailModal(true);
    setLoadingJobs(true);
    setCustomerJobs([]);
    setCustomerInvoices([]);

    try {
      // Load jobs and invoices for this customer in parallel
      const [jobsResult, invoicesResult] = await Promise.all([
        supabaseData
          .from('jobs')
          .select(`
            *,
            invoices!invoices_job_id_fkey (id, invoice_number, total, status)
          `)
          .eq('customer_id', customer.id)
          .order('created_at', { ascending: false }),
        supabaseData
          .from('invoices')
          .select('*')
          .eq('customer_id', customer.id)
          .order('created_at', { ascending: false })
      ]);

      if (jobsResult.error) throw jobsResult.error;
      setCustomerJobs(jobsResult.data || []);
      setCustomerInvoices(invoicesResult.data || []);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed': return colors.success;
      case 'in-progress': return colors.info;
      case 'scheduled': return colors.warning;
      case 'cancelled': return colors.error;
      default: return colors.textMuted;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (savingRecord) return;
    setSavingRecord(true);

    try {
      // Free tier is capped on total customers. Block new customer creation once
      // the cap is hit and steer them to upgrade (does not affect edits or leads).
      if (addType === 'customer' && !editingItem && tier === 'free') {
        const maxCustomers = getLimit('maxCustomers');
        const { count } = await supabaseData
          .from('customers')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId);
        if ((count || 0) >= maxCustomers) {
          showAlert(
            `The Free plan includes up to ${maxCustomers} customers. Upgrade your plan to add more.`,
            { title: 'Customer limit reached' }
          );
          return;
        }
      }

      if (addType === 'customer') {
        // Save customer with only customer-relevant fields
        const customerData = {
          name: `${formData.first_name} ${formData.last_name}`.trim(),
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          type: formData.type,
          notes: formData.notes,
          sms_consent: formData.sms_consent,
          company_id: companyId
        };

        if (editingItem) {
          const { error, queued } = await updateWithSync('customers', editingItem.id, customerData);
          if (error) throw new Error(error);
          if (queued) showSuccess('Customer saved offline - will sync when connected');
        } else {
          const { error, queued } = await insertWithSync('customers', customerData);
          if (error) throw new Error(error);
          if (queued) showSuccess('Customer saved offline - will sync when connected');
        }
      } else {
        // Save lead with offline sync support
        const leadData = {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          source: formData.lead_source || 'direct',
          sub_source: formData.sub_source || null,
          referrer_name: formData.lead_source === 'referral' ? formData.referrer_name : null,
          notes: formData.notes,
          status: 'new',
          sms_consent: formData.sms_consent
        };

        if (editingItem) {
          const { error, queued } = await updateWithSync('leads', editingItem.id, leadData);
          if (error) throw new Error(error);
          if (queued) showSuccess('Lead saved offline - will sync when connected');
        } else {
          const { error, queued } = await insertWithSync('leads', leadData);
          if (error) throw new Error(error);
          if (queued) showSuccess('Lead saved offline - will sync when connected');
        }
      }

      setShowAddModal(false);
      setEditingItem(null);
      resetForm();
      // Only reload if online, otherwise data will refresh on sync
      if (isOnline()) {
        loadData();
      }
    } catch (error) {
      showError('Error saving: ' + error.message);
    } finally {
      setSavingRecord(false);
    }
  };

  const handleConvertToCustomer = async (lead) => {
    const confirmed = await showConfirm(`Convert "${lead.name}" to customer?`, {
      title: 'Convert Lead',
      confirmText: 'Convert'
    });
    if (!confirmed) return;

    try {
      // Create customer from lead
      const { error: customerError } = await supabaseData
        .from('customers')
        .insert([{
          name: lead.name,
          phone: lead.phone,
          email: lead.email,
          address: lead.address,
          city: lead.city,
          state: lead.state,
          zip: lead.zip,
          type: 'residential',
          lead_source: lead.source,
          sub_source: lead.sub_source,
          referrer_name: lead.referrer_name,
          notes: lead.notes,
          company_id: companyId
        }]);
      
      if (customerError) throw customerError;
      
      // Update lead status to converted
      const { error: leadError } = await supabaseData
        .from('leads')
        .update({ status: 'converted', converted_at: new Date().toISOString() })
        .eq('id', lead.id);
      
      if (leadError) throw leadError;

      showSuccess('Lead converted to customer!');
      loadData();
      setActiveTab('customers');
    } catch (error) {
      showError('Error converting lead: ' + error.message);
    }
  };

  const handleArchive = async (item) => {
    // Only customers can be archived for now (not leads)
    if (item.record_type !== 'customer') return;
    const alreadyArchived = !!item.archived_at;
    const confirmed = await showConfirm(
      alreadyArchived
        ? `Unarchive ${item.name || 'this customer'}?`
        : `Archive ${item.name || 'this customer'}? You can bring them back anytime via "Show Archived".`,
      {
        title: alreadyArchived ? 'Unarchive Customer' : 'Archive Customer',
        confirmText: alreadyArchived ? 'Unarchive' : 'Archive'
      }
    );
    if (!confirmed) return;

    try {
      const update = alreadyArchived
        ? { archived_at: null, archived_reason: null }
        : { archived_at: new Date().toISOString(), archived_reason: 'manual' };

      const { error } = await supabaseData
        .from('customers')
        .update(update)
        .eq('id', item.id);

      if (error) throw error;

      setData(prev => prev.map(d => d.id === item.id ? { ...d, ...update } : d));
    } catch (error) {
      showError('Error ' + (alreadyArchived ? 'unarchiving' : 'archiving') + ': ' + error.message);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = await showConfirm(`Delete ${item.name || 'this record'}?`, {
      title: 'Delete Record',
      confirmText: 'Delete',
      confirmStyle: 'danger'
    });
    if (!confirmed) return;
    
    try {
      const table = item.record_type === 'customer' ? 'customers' : 'leads';
      const { error } = await supabaseData
        .from(table)
        .delete()
        .eq('id', item.id);

      if (error) throw error;
      // Remove from state directly to preserve scroll position
      setData(prev => prev.filter(d => d.id !== item.id));
      setFilteredData(prev => prev.filter(d => d.id !== item.id));
    } catch (error) {
      showError('Error deleting: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setAddType(item.record_type);
    setFormData({
      name: item.name || '',
      first_name: item.first_name || '',
      last_name: item.last_name || '',
      phone: item.phone || '',
      email: item.email || '',
      address: item.address || '',
      city: item.city || '',
      state: item.state || '',
      zip: item.zip || '',
      type: item.type || 'residential',
      lead_source: item.lead_source || item.source || '',
      sub_source: item.sub_source || '',
      referrer_name: item.referrer_name || '',
      notes: item.notes || '',
      sms_consent: item.sms_consent !== false  // Default true unless explicitly false
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      type: 'residential',
      lead_source: '',
      sub_source: '',
      referrer_name: '',
      notes: '',
      sms_consent: true
    });
  };

  const openAddModal = (type) => {
    setAddType(type);
    setEditingItem(null);
    resetForm();
    setShowAddModal(true);
  };

  // Send signup link to customer
  // Create signup link and send via API (email, or SMS if texting enabled)
  const handleSendSignupLink = async () => {
    if (signupMethod === 'sms' && !signupPhone.trim()) {
      showError('Please enter a phone number');
      return;
    }
    if (signupMethod === 'email' && !signupEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    setSendingSignupLink(true);
    try {
      const response = await fetch('/api/customer-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send',
          phone: signupPhone || undefined,
          email: signupEmail || undefined,
          method: signupMethod,
          companyId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send signup link');
      }

      setSignupLinkSent(true);
      setSignupLinkUrl(data.signupUrl);

      if (data.smsSent) {
        showSuccess('Signup link sent via text!');
      } else if (data.emailSent) {
        showSuccess('Signup link sent via email!');
      } else {
        showAlert('Signup link created. Delivery failed - you can copy the link and send manually.');
      }
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSendingSignupLink(false);
    }
  };

  // Create signup link and open native SMS app on user's phone
  const handleSendFromMyPhone = async () => {
    if (!signupPhone.trim()) {
      showError('Please enter a phone number');
      return;
    }

    setSendingSignupLink(true);
    try {
      const response = await fetch('/api/customer-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send',
          phone: signupPhone,
          method: 'none', // Don't send — just create the link
          companyId
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create link');

      const linkUrl = data.signupUrl;
      const smsBody = encodeURIComponent(`Hi! Please fill out your info here: ${linkUrl}`);
      const cleanPhone = signupPhone.replace(/\D/g, '');

      // Open native SMS app with pre-filled message and recipient
      window.location.href = `sms:${cleanPhone}?body=${smsBody}`;

      setSignupLinkSent(true);
      setSignupLinkUrl(linkUrl);
    } catch (error) {
      showError('Error: ' + error.message);
    } finally {
      setSendingSignupLink(false);
    }
  };

  // Export customers to CSV
  const handleExportCSV = () => {
    const customers = data.filter(d => d.record_type === 'customer');

    if (customers.length === 0) {
      showError('No customers to export');
      return;
    }

    const headers = ['name', 'email', 'phone', 'address', 'city', 'state', 'zip', 'notes', 'created_at'];
    const rows = customers.map(c => [
      c.name || '',
      c.email || '',
      c.phone || '',
      c.address || '',
      c.city || '',
      c.state || '',
      c.zip || '',
      (c.notes || '').replace(/"/g, '""'), // Escape quotes
      c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    showSuccess(`Exported ${customers.length} customers`);
  };

  // Parse CSV file
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    // Parse header
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));

    // Parse rows
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }

    return rows;
  };

  // Handle file selection for import
  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const parsed = parseCSV(text);
      setImportPreview(parsed.slice(0, 5)); // Show first 5 rows
    };
    reader.readAsText(file);
  };

  // Import customers from CSV
  const handleImportCSV = async () => {
    if (!importFile) {
      showError('Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target.result;
        const rows = parseCSV(text);

        if (rows.length === 0) {
          showError('No valid data found in CSV');
          setImporting(false);
          return;
        }

        let imported = 0;
        let skipped = 0;
        const errors = [];

        // Get existing customers for duplicate check
        const existingCustomers = data.filter(d => d.record_type === 'customer');
        const existingPhones = new Set(existingCustomers.map(c => (c.phone || '').replace(/\D/g, '')));
        const existingEmails = new Set(existingCustomers.map(c => (c.email || '').toLowerCase()));

        for (const row of rows) {
          // Map CSV columns to customer fields
          const customer = {
            company_id: companyId,
            name: row.name || row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown',
            email: row.email || null,
            phone: row.phone || row.phone_number || null,
            address: row.address || row.street || null,
            city: row.city || null,
            state: row.state || null,
            zip: row.zip || row.zipcode || row.postal_code || null,
            notes: row.notes || null,
            source: 'csv_import'
          };

          // Check for duplicates
          const normalizedPhone = (customer.phone || '').replace(/\D/g, '');
          const normalizedEmail = (customer.email || '').toLowerCase();

          if ((normalizedPhone && existingPhones.has(normalizedPhone)) ||
              (normalizedEmail && existingEmails.has(normalizedEmail))) {
            skipped++;
            continue;
          }

          try {
            const { error } = await supabaseData
              .from('customers')
              .insert(customer);

            if (error) throw error;

            imported++;
            // Add to existing sets to prevent duplicates within same import
            if (normalizedPhone) existingPhones.add(normalizedPhone);
            if (normalizedEmail) existingEmails.add(normalizedEmail);
          } catch (err) {
            errors.push(`Row ${imported + skipped + 1}: ${err.message}`);
          }
        }

        setImportResult({ imported, skipped, errors });
        if (imported > 0) {
          loadData(); // Refresh the list
        }
        setImporting(false);
      };
      reader.readAsText(importFile);
    } catch (error) {
      showError('Error importing: ' + error.message);
      setImporting(false);
    }
  };

  // Download sample CSV template
  const downloadSampleCSV = () => {
    const sample = `name,email,phone,address,city,state,zip,notes
"John Smith","john@example.com","555-123-4567","123 Main St","Austin","TX","78701","Good customer"
"Jane Doe","jane@example.com","555-987-6543","456 Oak Ave","Dallas","TX","75201",""`;

    const blob = new Blob([sample], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer-import-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (item) => {
    if (item.record_type === 'customer') return colors.success;
    if (item.status === 'new') return colors.info;
    if (item.status === 'contacted') return colors.warning;
    if (item.status === 'converted') return colors.success;
    return colors.textMuted;
  };

  const getStatusLabel = (item) => {
    if (item.record_type === 'customer') return 'Customer';
    return item.status || 'New Lead';
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '0.5rem' : '1rem' }}>
      {/* Mobile CSS overrides */}
      <style>{`
        @media (max-width: 600px) {
          .customers-search-box { min-width: 100% !important; }
          .customers-header-btns { flex-direction: column; width: 100%; }
          .customers-header-btns button { width: 100%; justify-content: center; }
          .customers-tabs { width: 100%; }
          .customers-tabs button { flex: 1; padding: 0.6rem 0.5rem !important; font-size: 0.85rem !important; }
        }
      `}</style>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
            👥 Customers & Leads
          </h1>
          <p style={{ margin: 0, color: colors.textMuted }}>
            Manage your contacts and sales pipeline
          </p>
        </div>
        <div className="customers-header-btns" style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => openAddModal('lead')}
            style={{
              padding: '0.75rem 1.5rem',
              background: colors.info,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Target size={20} />
            New Lead
          </button>
          <button
            onClick={() => openAddModal('customer')}
            style={{
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={20} />
            New Customer
          </button>
          <button
            onClick={() => {
              setSignupPhone('');
              setSignupEmail('');
              setSignupMethod('sms');
              setSignupLinkSent(false);
              setSignupLinkUrl('');
              setShowSignupLinkModal(true);
            }}
            style={{
              padding: '0.75rem 1rem',
              background: colors.bgInput,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="Send signup link to customer"
          >
            <Link size={18} />
            {!isMobile && 'Send Link'}
          </button>
          <button
            onClick={() => {
              setImportFile(null);
              setImportPreview([]);
              setImportResult(null);
              setShowImportModal(true);
            }}
            style={{
              padding: '0.75rem 1rem',
              background: colors.bgInput,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="Import customers from CSV"
          >
            <Upload size={18} />
            {!isMobile && 'Import'}
          </button>
          <button
            onClick={handleExportCSV}
            style={{
              padding: '0.75rem 1rem',
              background: colors.bgInput,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="Export customers to CSV"
          >
            <Download size={18} />
            {!isMobile && 'Export'}
          </button>
        </div>
      </div>

      {/* Tabs + Search */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          {/* Filter Tabs */}
          <div className="customers-tabs" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('all')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'all' ? colors.accent : colors.bgInput,
                color: activeTab === 'all' ? '#fff' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📋 All ({data.length})
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'leads' ? colors.info : colors.bgInput,
                color: activeTab === 'leads' ? '#fff' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🎯 Leads ({data.filter(d => d.record_type === 'lead').length})
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              style={{
                padding: '0.75rem 1.5rem',
                background: activeTab === 'customers' ? colors.success : colors.bgInput,
                color: activeTab === 'customers' ? '#000' : colors.textMuted,
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ✅ Customers ({data.filter(d => d.record_type === 'customer').length})
            </button>
          </div>

          {/* Search */}
          <div className="customers-search-box" style={{ position: 'relative', minWidth: 'min(250px, 100%)' }}>
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: colors.textMuted
              }}
            />
            <input
              type="text"
              placeholder="Search by name, phone, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 3rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '0.95rem'
              }}
            />
          </div>
        </div>
      </div>

      {/* Sort Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
          Showing {filteredData.length} of {data.length}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowArchived(v => !v)}
            style={{
              padding: '0.4rem 0.75rem',
              background: showArchived ? colors.accent : 'transparent',
              border: `1px solid ${showArchived ? colors.accent : colors.border}`,
              borderRadius: '6px',
              color: showArchived ? '#000' : colors.textMuted,
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            <Archive size={14} />
            {showArchived ? 'Viewing Archived' : 'Show Archived'}
          </button>
          <span style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '0.4rem 0.75rem',
              background: colors.bgInput,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              color: colors.text,
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="a-z">A-Z</option>
            <option value="z-a">Z-A</option>
          </select>
        </div>
      </div>

      {/* Data Grid */}
      {filteredData.length === 0 ? (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '4rem 2rem',
          textAlign: 'center',
          border: `2px dashed ${colors.border}`
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            {activeTab === 'leads' ? '🎯' : activeTab === 'customers' ? '✅' : '👥'}
          </div>
          <h3 style={{ color: colors.textMuted, marginBottom: '0.5rem' }}>
            No {activeTab === 'all' ? 'contacts' : activeTab} yet
          </h3>
          <p style={{ color: '#666' }}>
            Click "New {activeTab === 'leads' ? 'Lead' : 'Customer'}" to get started
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
        <div ref={listRef} style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(350px, 100%), 1fr))',
          gap: '1.5rem'
        }}>
          {filteredData.map(item => {
            const displayName = item.name || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown';
            
            return (
              <div
                key={`${item.record_type}-${item.id}`}
                onClick={() => openCustomerDetail(item)}
                style={{
                  background: colors.bgCard,
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: `1px solid ${colors.border}`,
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, transform 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = colors.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0.35rem 0.75rem',
                  background: `${getStatusColor(item)}20`,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  color: getStatusColor(item),
                  textTransform: 'capitalize'
                }}>
                  {getStatusLabel(item)}
                </div>

                {/* Name */}
                <h3 style={{
                  fontSize: '1.25rem',
                  margin: '0 0 1rem 0',
                  color: colors.text,
                  paddingRight: '100px'
                }}>
                  {displayName}
                </h3>

                {/* Contact Info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {item.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: colors.textMuted }}>
                      <Phone size={16} />
                      <a href={`tel:${item.phone}`} style={{ color: colors.info, textDecoration: 'none' }}>
                        {item.phone}
                      </a>
                    </div>
                  )}
                  {item.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: colors.textMuted }}>
                      <Mail size={16} />
                      <a href={`mailto:${item.email}`} style={{ color: colors.info, textDecoration: 'none' }}>
                        {item.email}
                      </a>
                    </div>
                  )}
                  {item.address && (
                    <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', color: colors.textMuted }}>
                      <MapPin size={16} style={{ marginTop: '2px' }} />
                      <span style={{ fontSize: '0.9rem' }}>
                        {item.address}
                        {item.city && `, ${item.city}`}
                        {item.state && `, ${item.state}`}
                        {item.zip && ` ${item.zip}`}
                      </span>
                    </div>
                  )}
                  {/* Lead Source Display */}
                  {item.record_type === 'lead' && item.source && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: colors.textMuted }}>
                      <Target size={16} />
                      <span style={{ fontSize: '0.9rem' }}>
                        {leadSourceOptions.find(o => o.value === item.source)?.label || item.source}
                        {item.referrer_name && ` - ${item.referrer_name}`}
                        {item.sub_source && !item.referrer_name && ` (${item.sub_source})`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Activity: jobs + quotes (customers only) */}
                {item.record_type === 'customer' && (() => {
                  const { active, history } = getCustomerActivity(item.id);
                  if (active.length === 0 && history.length === 0) return null;
                  const isExpanded = !!expandedActivity[item.id];
                  const HISTORY_PREVIEW = 2;
                  const shownHistory = isExpanded ? history : history.slice(0, HISTORY_PREVIEW);
                  const hiddenCount = history.length - shownHistory.length;
                  const row = (a) => (
                    <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.2rem 0' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                      <span style={{ color: colors.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.label}</span>
                      {a.amount ? <span style={{ color: colors.textMuted, flexShrink: 0 }}>${Number(a.amount).toLocaleString()}</span> : null}
                    </div>
                  );
                  return (
                    <div style={{ marginBottom: '1.25rem', padding: '0.75rem 0.9rem', background: colors.bgInput, borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                      {active.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.textMuted, marginBottom: '0.3rem', fontWeight: 'bold' }}>Active</div>
                          {active.map(row)}
                        </>
                      )}
                      {history.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: colors.textMuted, margin: `${active.length > 0 ? '0.6rem' : '0'} 0 0.3rem`, fontWeight: 'bold' }}>History</div>
                          {shownHistory.map(row)}
                          {history.length > HISTORY_PREVIEW && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpandedActivity(prev => ({ ...prev, [item.id]: !isExpanded })); }}
                              style={{ marginTop: '0.4rem', background: 'none', border: 'none', color: colors.accent, fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', padding: 0 }}
                            >
                              {isExpanded ? 'Show less' : `More (${hiddenCount})`}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                  {item.record_type === 'lead' && item.status !== 'converted' && item.status !== 'closed-lost' && (
                    <>
                      <button
                        onClick={() => {
                          setSelectedCustomer(item);
                          setShowExpressJob(true);
                        }}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: colors.accent,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          minHeight: '44px'
                        }}
                      >
                        <Briefcase size={16} />
                        Job Now
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCustomer(item);
                          setShowScheduleModal(true);
                        }}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: colors.info,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          minHeight: '44px'
                        }}
                      >
                        <Calendar size={16} />
                        Schedule
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCustomer(item);
                          setShowLostModal(true);
                        }}
                        style={{
                          padding: '0.75rem',
                          background: colors.error,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          minHeight: '44px'
                        }}
                      >
                        <XCircle size={16} />
                        Lost
                      </button>
                    </>
                  )}
                  {item.record_type === 'customer' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Navigate to express job workflow with customer prefilled
                        if (onNavigate) {
                          onNavigate('express-job', { prefilledCustomer: item });
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: colors.accent,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        minHeight: '44px'
                      }}
                    >
                      <Briefcase size={16} />
                      Create Job
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCustomer(item);
                      setShowCommunications(true);
                    }}
                    style={{
                      padding: '0.75rem',
                      background: colors.info,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '44px'
                    }}
                    title="Send message"
                  >
                    <MessageSquare size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    style={{
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.info,
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  {item.record_type === 'customer' && (
                    <button
                      onClick={() => handleArchive(item)}
                      title={item.archived_at ? 'Unarchive' : 'Archive'}
                      style={{
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: item.archived_at ? colors.accent : colors.textMuted,
                        cursor: 'pointer'
                      }}
                    >
                      <Archive size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(item)}
                    style={{
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.error,
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {/* Alphabet Quick-Jump Bar */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1px',
          padding: '0.25rem 0',
          position: 'sticky',
          top: '1rem',
          alignSelf: 'flex-start',
          userSelect: 'none'
        }}>
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => {
            const hasMatch = filteredData.some(item => {
              const name = (item.name || `${item.first_name || ''} ${item.last_name || ''}`).trim();
              return name.toUpperCase().startsWith(letter);
            });
            return (
              <button
                key={letter}
                onClick={() => {
                  if (!hasMatch) return;
                  const container = listRef.current;
                  if (!container) return;
                  const idx = filteredData.findIndex(item => {
                    const name = (item.name || `${item.first_name || ''} ${item.last_name || ''}`).trim();
                    return name.toUpperCase().startsWith(letter);
                  });
                  if (idx >= 0) {
                    const cards = container.children;
                    if (cards[idx]) {
                      cards[idx].scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '1px 4px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  color: hasMatch ? colors.accent : colors.border,
                  cursor: hasMatch ? 'pointer' : 'default',
                  lineHeight: '1.3',
                  minWidth: '20px',
                  textAlign: 'center'
                }}
              >
                {letter}
              </button>
            );
          })}
        </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            border: `1px solid ${colors.border}`,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: colors.bgCard,
              zIndex: 1
            }}>
              <h3 style={{ margin: 0, color: colors.text }}>
                {editingItem ? 'Edit' : 'New'} {addType === 'customer' ? 'Customer' : 'Lead'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ padding: '2rem' }}>
              {addType === 'customer' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    >
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                    </select>
                  </div>
                </>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    maxLength={2}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      textTransform: 'uppercase'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    maxLength={5}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              {/* Lead Source Section - only show for leads */}
              {addType === 'lead' && (
                <>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                      Lead Source
                    </label>
                    <select
                      value={formData.lead_source}
                      onChange={(e) => setFormData({ ...formData, lead_source: e.target.value, sub_source: '', referrer_name: '' })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: colors.text,
                        fontSize: '1rem'
                      }}
                    >
                      {leadSourceOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sub-source for certain sources */}
                  {['google_ads', 'facebook', 'instagram'].includes(formData.lead_source) && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Campaign / Ad Name (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.sub_source}
                        onChange={(e) => setFormData({ ...formData, sub_source: e.target.value })}
                        placeholder="e.g., Spring Sale 2025, Emergency Repairs"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}

                  {/* Referrer name for referral source */}
                  {formData.lead_source === 'referral' && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                        Referred By
                      </label>
                      <input
                        type="text"
                        value={formData.referrer_name}
                        onChange={(e) => setFormData({ ...formData, referrer_name: e.target.value })}
                        placeholder="e.g., John Smith, ABC Realty, Previous Customer"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}

                  {/* Other source description */}
                  {formData.lead_source === 'other' && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Describe Source
                      </label>
                      <input
                        type="text"
                        value={formData.sub_source}
                        onChange={(e) => setFormData({ ...formData, sub_source: e.target.value })}
                        placeholder="e.g., Trade show, Mailer, Billboard"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* SMS Consent - shown when phone is entered */}
              {formData.phone && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <SMSConsentInline
                    checked={formData.sms_consent}
                    onChange={(checked) => setFormData({ ...formData, sms_consent: checked })}
                    companyName="your service provider"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={savingRecord}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: savingRecord ? colors.border : colors.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: savingRecord ? 'not-allowed' : 'pointer',
                  opacity: savingRecord ? 0.7 : 1
                }}
              >
                {savingRecord ? 'Saving...' : `${editingItem ? 'Update' : 'Create'} ${addType === 'customer' ? 'Customer' : 'Lead'}`}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ExpressJob Modal */}
      {showExpressJob && selectedCustomer && (
        <ExpressJob
          companyId={companyId}
          prefilledCustomer={selectedCustomer}
          onComplete={() => {
            setShowExpressJob(false);
            setSelectedCustomer(null);
            loadData();
          }}
          onCancel={() => {
            setShowExpressJob(false);
            setSelectedCustomer(null);
          }}
        />
      )}

      {/* Schedule Modal */}
      {showScheduleModal && selectedCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            border: `1px solid ${colors.border}`,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: colors.bgCard,
              zIndex: 1
            }}>
              <h2 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={24} />
                Schedule Job
              </h2>
              <button
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedCustomer(null);
                  setScheduleDate('');
                  setScheduleTime('9:00');
                  setScheduleDuration(2);
                  setScheduleJobType('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {/* Customer Info */}
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                border: `1px solid ${colors.border}`
              }}>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                  {selectedCustomer.record_type === 'lead' ? 'Lead' : 'Customer'}
                </div>
                <div style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {safeString(selectedCustomer.name) || `${safeString(selectedCustomer.first_name)} ${safeString(selectedCustomer.last_name)}`.trim()}
                </div>
                {selectedCustomer.phone && (
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    {safeString(selectedCustomer.phone)}
                  </div>
                )}
              </div>

              {/* Date Picker */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Date *
                </label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Time Picker */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  <Clock size={18} />
                  Time *
                </label>
                <select
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  {['7:00', '7:30', '8:00', '8:30', '9:00', '9:30', '10:00', '10:30',
                    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
                    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00'
                  ].map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              {/* Duration */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  Estimated Duration *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)', gap: '0.5rem' }}>
                  {[0.5, 1, 2, 4, 8].map(hours => (
                    <button
                      key={hours}
                      type="button"
                      onClick={() => setScheduleDuration(hours)}
                      style={{
                        padding: '0.75rem 0.5rem',
                        background: scheduleDuration === hours ? colors.info : colors.bgInput,
                        border: scheduleDuration === hours ? `2px solid ${colors.info}` : `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: scheduleDuration === hours ? '#fff' : colors.text,
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minHeight: '44px'
                      }}
                    >
                      {hours}h
                    </button>
                  ))}
                </div>
              </div>

              {/* Job Type */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.textMuted,
                  fontSize: '0.9rem'
                }}>
                  Job Type (optional)
                </label>
                <input
                  type="text"
                  value={scheduleJobType}
                  onChange={(e) => setScheduleJobType(e.target.value)}
                  placeholder="e.g., Spring Repair, Door Installation"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setSelectedCustomer(null);
                    setScheduleDate('');
                    setScheduleTime('9:00');
                    setScheduleDuration(2);
                    setScheduleJobType('');
                  }}
                  disabled={scheduling}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: scheduling ? 'not-allowed' : 'pointer',
                    minHeight: '44px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!scheduleDate) {
                      showAlert('Please select a date', { title: 'Missing Date' });
                      return;
                    }

                    setScheduling(true);
                    try {
                      // If this is a lead, convert to customer first
                      let customerId = selectedCustomer.id;

                      if (selectedCustomer.record_type === 'lead') {
                        // Create customer from lead
                        const { data: newCustomer, error: customerError } = await supabaseData
                          .from('customers')
                          .insert([{
                            name: safeString(selectedCustomer.name),
                            phone: safeString(selectedCustomer.phone),
                            email: safeString(selectedCustomer.email),
                            address: safeString(selectedCustomer.address),
                            city: safeString(selectedCustomer.city),
                            state: safeString(selectedCustomer.state),
                            zip: safeString(selectedCustomer.zip),
                            type: 'residential',
                            lead_source: safeString(selectedCustomer.source),
                            sub_source: safeString(selectedCustomer.sub_source),
                            referrer_name: safeString(selectedCustomer.referrer_name),
                            notes: safeString(selectedCustomer.notes),
                            company_id: companyId
                          }])
                          .select()
                          .single();

                        if (customerError) throw customerError;
                        customerId = newCustomer.id;

                        // Update lead status to converted
                        await supabaseData
                          .from('leads')
                          .update({ status: 'converted', converted_at: new Date().toISOString() })
                          .eq('id', selectedCustomer.id);
                      }

                      // Generate job number
                      const date = new Date();
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
                      const jobNumber = `J-${year}${month}-${random}`;

                      // Create scheduled job
                      const { error: jobError } = await supabaseData
                        .from('jobs')
                        .insert([{
                          company_id: companyId,
                          customer_id: customerId,
                          job_number: jobNumber,
                          job_type: scheduleJobType || null,
                          status: 'scheduled',
                          scheduled_date: scheduleDate,
                          scheduled_time: scheduleTime,
                          estimated_duration: scheduleDuration,
                          notes: null
                        }]);

                      if (jobError) throw jobError;

                      showSuccess(`Job ${jobNumber} scheduled for ${scheduleDate} at ${scheduleTime}`);
                      setShowScheduleModal(false);
                      setSelectedCustomer(null);
                      setScheduleDate('');
                      setScheduleTime('9:00');
                      setScheduleDuration(2);
                      setScheduleJobType('');
                      loadData();
                    } catch (error) {
                      showError('Failed to schedule job: ' + error.message);
                    } finally {
                      setScheduling(false);
                    }
                  }}
                  disabled={!scheduleDate || scheduling}
                  style={{
                    flex: 1,
                    padding: '1rem',
                    background: scheduleDate && !scheduling ? colors.info : colors.border,
                    border: 'none',
                    borderRadius: '8px',
                    color: scheduleDate && !scheduling ? '#fff' : '#666',
                    fontWeight: 'bold',
                    cursor: scheduleDate && !scheduling ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    minHeight: '44px'
                  }}
                >
                  <Calendar size={20} />
                  {scheduling ? 'Scheduling...' : 'Schedule Job'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark Lost Modal */}
      {showLostModal && selectedCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h3 style={{ color: colors.text, marginBottom: '1rem' }}>Mark Lead as Lost</h3>
            <p style={{ color: colors.textMuted, marginBottom: '1rem' }}>
              Why did you lose {selectedCustomer.name}?
            </p>
            <textarea
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              placeholder="e.g., Price too high, chose competitor, not ready..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '1rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: colors.text,
                fontSize: '1rem',
                marginBottom: '1rem',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowLostModal(false);
                  setSelectedCustomer(null);
                  setLostReason('');
                }}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.text,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!lostReason.trim()) {
                    showAlert('Please provide a reason', { title: 'Reason Required' });
                    return;
                  }
                  try {
                    const { error } = await supabaseData
                      .from('leads')
                      .update({
                        status: 'closed-lost',
                        closed_reason: lostReason,
                        closed_at: new Date().toISOString()
                      })
                      .eq('id', selectedCustomer.id);

                    if (error) throw error;

                    showSuccess('Lead marked as lost');
                    setShowLostModal(false);
                    setSelectedCustomer(null);
                    setLostReason('');
                    loadData();
                  } catch (error) {
                    showError('Error: ' + error.message);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: colors.error,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Mark as Lost
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Communications Modal */}
      {showCommunications && selectedCustomer && (
        <CustomerCommunications
          customer={selectedCustomer}
          companyId={companyId}
          onClose={() => {
            setShowCommunications(false);
            setSelectedCustomer(null);
          }}
        />
      )}

      {/* Customer Detail Modal */}
      {showDetailModal && detailCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            border: `1px solid ${colors.border}`,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: colors.bgCard,
              zIndex: 1
            }}>
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  background: `${getStatusColor(detailCustomer)}20`,
                  color: getStatusColor(detailCustomer),
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  textTransform: 'capitalize'
                }}>
                  {getStatusLabel(detailCustomer)}
                </div>
                <h2 style={{ margin: 0, color: colors.text, fontSize: '1.5rem' }}>
                  {detailCustomer.name || `${detailCustomer.first_name || ''} ${detailCustomer.last_name || ''}`.trim() || 'Unknown'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setDetailCustomer(null);
                  setCustomerJobs([]);
                  setCustomerInvoices([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {/* Contact Info */}
              <div style={{
                background: colors.bgInput,
                borderRadius: '10px',
                padding: '1.25rem',
                marginBottom: '1.5rem',
                border: `1px solid ${colors.border}`
              }}>
                <h3 style={{ margin: '0 0 1rem 0', color: colors.text, fontSize: '1rem' }}>
                  Contact Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  {detailCustomer.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Phone size={18} color={colors.textMuted} />
                      <a href={`tel:${detailCustomer.phone}`} style={{ color: colors.info, textDecoration: 'none' }}>
                        {detailCustomer.phone}
                      </a>
                    </div>
                  )}
                  {detailCustomer.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <Mail size={18} color={colors.textMuted} />
                      <a href={`mailto:${detailCustomer.email}`} style={{ color: colors.info, textDecoration: 'none' }}>
                        {detailCustomer.email}
                      </a>
                    </div>
                  )}
                </div>
                {detailCustomer.address && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1rem' }}>
                    <MapPin size={18} color={colors.textMuted} style={{ marginTop: '2px' }} />
                    <span style={{ color: colors.text }}>
                      {detailCustomer.address}
                      {detailCustomer.city && `, ${detailCustomer.city}`}
                      {detailCustomer.state && `, ${detailCustomer.state}`}
                      {detailCustomer.zip && ` ${detailCustomer.zip}`}
                    </span>
                  </div>
                )}
                {detailCustomer.notes && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Notes</div>
                    <div style={{ color: colors.text }}>{detailCustomer.notes}</div>
                  </div>
                )}
              </div>

              {/* Card on File - only show for customers with Stripe Connect */}
              {detailCustomer.record_type === 'customer' && company?.stripe_connect_id && company?.stripe_charges_enabled && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <CustomerCardOnFile
                    customer={detailCustomer}
                    companyId={companyId}
                    stripeConnected={true}
                    isDemo={company?.is_demo || false}
                    onUpdate={(updatedCustomer) => {
                      setDetailCustomer(updatedCustomer);
                      // Also update in the main data list
                      setData(prevData => prevData.map(d =>
                        d.id === updatedCustomer.id ? { ...d, ...updatedCustomer } : d
                      ));
                    }}
                  />
                </div>
              )}

              {/* Job History */}
              <div>
                <h3 style={{ margin: '0 0 1rem 0', color: colors.text, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Briefcase size={18} />
                  Job History ({customerJobs.length})
                </h3>

                {loadingJobs ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
                    Loading jobs...
                  </div>
                ) : customerJobs.length === 0 ? (
                  <div style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: colors.textMuted,
                    background: colors.bgInput,
                    borderRadius: '10px',
                    border: `1px dashed ${colors.border}`
                  }}>
                    No jobs yet for this customer
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {customerJobs.map(job => (
                      <div
                        key={job.id}
                        onClick={() => {
                          setShowDetailModal(false);
                          setDetailCustomer(null);
                          if (onNavigate) {
                            onNavigate('work-orders', { jobId: job.id, tab: 'jobs' });
                          }
                        }}
                        style={{
                          background: colors.bgInput,
                          borderRadius: '10px',
                          padding: '1rem',
                          border: `1px solid ${colors.border}`,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.accent}
                        onMouseLeave={(e) => e.currentTarget.style.borderColor = colors.border}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: colors.text, marginBottom: '0.25rem' }}>
                              {job.job_number}
                            </div>
                            {job.job_type && (
                              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                                {job.job_type}
                              </div>
                            )}
                          </div>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            background: `${getJobStatusColor(job.status)}20`,
                            color: getJobStatusColor(job.status),
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}>
                            {job.status?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                          <span style={{ color: colors.textMuted }}>
                            {job.scheduled_date ? formatDate(job.scheduled_date) : formatDate(job.created_at)}
                          </span>
                          {job.invoices && job.invoices.length > 0 && (
                            <span style={{ color: colors.success, fontWeight: '600' }}>
                              ${job.invoices[0].total?.toFixed(2) || '0.00'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment History */}
              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', color: colors.text, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <DollarSign size={18} />
                  Payment History {customerInvoices.length > 0 && `(${customerInvoices.length})`}
                </h3>

                {customerInvoices.length === 0 ? (
                  <p style={{ color: colors.textMuted, fontSize: '0.9rem', margin: 0 }}>
                    No invoices for this customer yet.
                  </p>
                ) : (
                <>
                  {/* Summary */}
                  {(() => {
                    const totalPaid = customerInvoices
                      .filter(inv => inv.status === 'paid')
                      .reduce((sum, inv) => sum + (parseFloat(inv.amount_paid) || parseFloat(inv.total) || 0), 0);
                    const totalOutstanding = customerInvoices
                      .filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled')
                      .reduce((sum, inv) => sum + ((parseFloat(inv.total) || 0) - (parseFloat(inv.amount_paid) || 0)), 0);
                    const lifetimeValue = customerInvoices
                      .reduce((sum, inv) => sum + (parseFloat(inv.total) || 0), 0);

                    return (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: '0.75rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{
                          padding: '0.75rem',
                          background: colors.bgInput,
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: `1px solid ${colors.border}`
                        }}>
                          <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase' }}>Paid</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: colors.success }}>${totalPaid.toFixed(2)}</div>
                        </div>
                        <div style={{
                          padding: '0.75rem',
                          background: colors.bgInput,
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: `1px solid ${colors.border}`
                        }}>
                          <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase' }}>Outstanding</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: totalOutstanding > 0 ? '#f39c12' : colors.textMuted }}>${totalOutstanding.toFixed(2)}</div>
                        </div>
                        <div style={{
                          padding: '0.75rem',
                          background: colors.bgInput,
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: `1px solid ${colors.border}`
                        }}>
                          <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase' }}>Lifetime</div>
                          <div style={{ fontSize: '1.1rem', fontWeight: '700', color: colors.text }}>${lifetimeValue.toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Invoice list */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {customerInvoices.map(inv => {
                      const balance = (parseFloat(inv.total) || 0) - (parseFloat(inv.amount_paid) || 0);
                      const statusColor = inv.status === 'paid' ? colors.success
                        : inv.status === 'cancelled' ? colors.textMuted
                        : balance > 0 ? '#f39c12' : colors.text;
                      return (
                        <div
                          key={inv.id}
                          onClick={() => {
                            setShowDetailModal(false);
                            setDetailCustomer(null);
                            if (onNavigate) {
                              onNavigate('work-orders', { tab: 'invoices' });
                            }
                          }}
                          style={{
                            background: colors.bgInput,
                            borderRadius: '8px',
                            padding: '0.75rem 1rem',
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: '600', color: colors.text, fontSize: '0.9rem' }}>
                              {inv.invoice_number || 'Invoice'}
                            </div>
                            <div style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                              {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: '600', color: colors.text, fontSize: '0.9rem' }}>
                              ${(parseFloat(inv.total) || 0).toFixed(2)}
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              color: statusColor,
                              textTransform: 'capitalize'
                            }}>
                              {inv.status || 'draft'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
                )}
              </div>

              {/* Actions */}
              <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {detailCustomer.record_type === 'customer' && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      if (onNavigate) {
                        onNavigate('quotes', { prefilledCustomer: detailCustomer });
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: colors.accent,
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      minHeight: '48px'
                    }}
                  >
                    <Plus size={18} />
                    Create New Job
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedCustomer(detailCustomer);
                    setShowCommunications(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: colors.info,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    minHeight: '48px'
                  }}
                >
                  <MessageSquare size={18} />
                  Send Message
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleEdit(detailCustomer);
                  }}
                  style={{
                    padding: '0.875rem 1.25rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    minHeight: '48px'
                  }}
                >
                  <Edit2 size={18} />
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Signup Link Modal */}
      {showSignupLinkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '400px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Send Signup Link
              </h2>
              <button
                onClick={() => setShowSignupLinkModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {!signupLinkSent ? (
                <>
                  <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Send the customer a link to fill out their own info.
                  </p>

                  {/* Method Toggle */}
                  <div style={{ display: 'flex', marginBottom: '1rem', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                    <button
                      onClick={() => setSignupMethod('sms')}
                      style={{
                        flex: 1, padding: '0.6rem',
                        background: signupMethod === 'sms' ? colors.accent : colors.bgInput,
                        color: signupMethod === 'sms' ? '#fff' : colors.textMuted,
                        border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                      }}
                    >
                      <Phone size={15} /> Text
                    </button>
                    <button
                      onClick={() => setSignupMethod('email')}
                      style={{
                        flex: 1, padding: '0.6rem',
                        background: signupMethod === 'email' ? colors.accent : colors.bgInput,
                        color: signupMethod === 'email' ? '#fff' : colors.textMuted,
                        border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                      }}
                    >
                      <Mail size={15} /> Email
                    </button>
                  </div>

                  {signupMethod === 'sms' ? (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={signupPhone}
                        onChange={(e) => setSignupPhone(e.target.value)}
                        placeholder="(555) 123-4567"
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="customer@example.com"
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          color: colors.text,
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  )}

                  {signupMethod === 'email' ? (
                    /* Email: always send via API */
                    <button
                      onClick={handleSendSignupLink}
                      disabled={sendingSignupLink}
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        background: colors.accent,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        cursor: sendingSignupLink ? 'wait' : 'pointer',
                        opacity: sendingSignupLink ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {sendingSignupLink ? (
                        <>
                          <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send Invite
                        </>
                      )}
                    </button>
                  ) : textingEnabled ? (
                    /* SMS with texting enabled: primary = send via Work Van, secondary = send from phone */
                    <>
                      <button
                        onClick={handleSendSignupLink}
                        disabled={sendingSignupLink}
                        style={{
                          width: '100%',
                          padding: '0.875rem',
                          background: colors.accent,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          cursor: sendingSignupLink ? 'wait' : 'pointer',
                          opacity: sendingSignupLink ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        {sendingSignupLink ? (
                          <>
                            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            Send via Work Van
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleSendFromMyPhone}
                        disabled={sendingSignupLink}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'transparent',
                          color: colors.textMuted,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          cursor: sendingSignupLink ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          marginTop: '0.5rem'
                        }}
                      >
                        <Phone size={15} />
                        Or send from my phone
                      </button>
                    </>
                  ) : (
                    /* SMS without texting: primary = send from phone, with upsell */
                    <>
                      <button
                        onClick={handleSendFromMyPhone}
                        disabled={sendingSignupLink}
                        style={{
                          width: '100%',
                          padding: '0.875rem',
                          background: colors.accent,
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          cursor: sendingSignupLink ? 'wait' : 'pointer',
                          opacity: sendingSignupLink ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        {sendingSignupLink ? (
                          <>
                            <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                            Creating link...
                          </>
                        ) : (
                          <>
                            <Phone size={18} />
                            Text from My Phone
                          </>
                        )}
                      </button>
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: `${colors.accent}15`,
                        border: `1px solid ${colors.accent}30`,
                        borderRadius: '8px',
                        fontSize: '0.8rem',
                        color: colors.textMuted,
                        textAlign: 'center'
                      }}>
                        Want to send texts directly from Work Van?{' '}
                        <span
                          onClick={() => {
                            setShowSignupLinkModal(false);
                            if (onNavigate) onNavigate('settings', { section: 'communications' });
                          }}
                          style={{ color: colors.accent, cursor: 'pointer', fontWeight: 'bold' }}
                        >
                          Set up messaging
                        </span>
                      </div>
                    </>
                  )}
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                    <CheckCircle size={48} color={colors.success} />
                    <h3 style={{ color: colors.text, marginTop: '1rem' }}>Invite Sent!</h3>
                    <p style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                      The customer will receive {signupMethod === 'email' ? 'an email' : 'a text'} with a link to fill out their information.
                    </p>
                  </div>
                  {signupLinkUrl && (
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.textMuted, fontSize: '0.85rem' }}>
                        Link (copy if needed):
                      </label>
                      <input
                        type="text"
                        value={signupLinkUrl}
                        readOnly
                        onClick={(e) => e.target.select()}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          background: colors.bgInput,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '6px',
                          color: colors.textMuted,
                          fontSize: '0.8rem'
                        }}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => setShowSignupLinkModal(false)}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflowY: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            border: `1px solid ${colors.border}`,
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: colors.bgCard
            }}>
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.25rem' }}>
                Import Customers
              </h2>
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              {/* Download Template */}
              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  onClick={downloadSampleCSV}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'transparent',
                    border: `1px solid ${colors.accent}`,
                    borderRadius: '6px',
                    color: colors.accent,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Download size={16} />
                  Download Sample Template
                </button>
              </div>

              {/* File Upload */}
              <div style={{
                border: `2px dashed ${colors.border}`,
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportFileSelect}
                  style={{ display: 'none' }}
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  style={{
                    cursor: 'pointer',
                    color: colors.textMuted
                  }}
                >
                  <Upload size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                  <div style={{ marginBottom: '0.5rem' }}>
                    {importFile ? importFile.name : 'Click to select CSV file'}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                    Supported columns: name, email, phone, address, city, state, zip, notes
                  </div>
                </label>
              </div>

              {/* Preview */}
              {importPreview.length > 0 && !importResult && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h4 style={{ color: colors.text, marginBottom: '0.75rem' }}>
                    Preview (first {importPreview.length} rows):
                  </h4>
                  <div style={{
                    background: colors.bgInput,
                    borderRadius: '8px',
                    padding: '0.75rem',
                    fontSize: '0.8rem',
                    overflowX: 'auto'
                  }}>
                    {importPreview.map((row, idx) => (
                      <div key={idx} style={{
                        padding: '0.5rem 0',
                        borderBottom: idx < importPreview.length - 1 ? `1px solid ${colors.border}` : 'none',
                        color: colors.text
                      }}>
                        <strong>{row.name || row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || '(no name)'}</strong>
                        {row.email && <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>{row.email}</span>}
                        {row.phone && <span style={{ color: colors.textMuted, marginLeft: '0.5rem' }}>{row.phone}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Import Result */}
              {importResult && (
                <div style={{
                  background: importResult.imported > 0 ? `${colors.success}20` : `${colors.warning}20`,
                  border: `1px solid ${importResult.imported > 0 ? colors.success : colors.warning}`,
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    {importResult.imported > 0 ? (
                      <CheckCircle size={20} color={colors.success} />
                    ) : (
                      <AlertCircle size={20} color={colors.warning} />
                    )}
                    <strong style={{ color: colors.text }}>Import Complete</strong>
                  </div>
                  <div style={{ color: colors.text, fontSize: '0.9rem' }}>
                    {importResult.imported} customers imported
                    {importResult.skipped > 0 && `, ${importResult.skipped} skipped (duplicates)`}
                  </div>
                  {importResult.errors.length > 0 && (
                    <div style={{ marginTop: '0.5rem', color: colors.error, fontSize: '0.8rem' }}>
                      Errors: {importResult.errors.slice(0, 3).join(', ')}
                      {importResult.errors.length > 3 && ` and ${importResult.errors.length - 3} more`}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setShowImportModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  {importResult ? 'Close' : 'Cancel'}
                </button>
                {!importResult && (
                  <button
                    onClick={handleImportCSV}
                    disabled={!importFile || importing}
                    style={{
                      flex: 1,
                      padding: '0.875rem',
                      background: !importFile ? colors.bgInput : colors.accent,
                      color: !importFile ? colors.textMuted : '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: !importFile || importing ? 'not-allowed' : 'pointer',
                      opacity: importing ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {importing ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={18} />
                        Import Customers
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
